import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import {
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Chip,
  Divider,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  IconButton,
  Menu,
  Select,
  Checkbox,
  Stack,
  Container
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ClearIcon from "@mui/icons-material/Clear";
import { getAllplaces, getSinglePlace, deletePlace, getActiveZones } from "../../api/placeApi";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { removeSelectedPlace, setSelectedPlace, setAllPlaces, removePlaceFromStore } from "../../reduxcomponents/slices/placesSlice";
import useSnackbar from "../../hooks/useSnackbar";
import CheckIcon from "@mui/icons-material/Check";
import { handleChangeRanking } from "../allPackages/rankingUtil"; // adjust path
import usePermissions from "../../hooks/UsePermissions";

const AllPlaces = () => {
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const getPermission = usePermissions();
  const canAddPackages = getPermission('places', 'add-packages') || getPermission('places', 'alter');
  const { zoneSegment, searchSegment } = useParams();
  const [searchParams] = useSearchParams();

  // Connect to Redux
  const storedPlaces = useSelector((state) => state.place?.allPlaces);
  const allPlaces = useMemo(() => (Array.isArray(storedPlaces) ? storedPlaces : []), [storedPlaces]);

  const [filteredPlaces, setFilteredPlaces] = useState([]);
  const [rankingLoading, setRankingLoading] = useState({});
  const [availableZones, setAvailableZones] = useState([]);

  const [visibleCount, setVisibleCount] = useState(9);

  // Infinite Scroll Observer
  const observerRef = useRef(null);
  const lastElementRef = useCallback((node) => {
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(entries => {
      // increase visible count when we intersect the bottom sentinel
      if (entries[0].isIntersecting) {
        setVisibleCount(prev => prev + 9);
      }
    });
    if (node) observerRef.current.observe(node);
  }, []);

  // Stop observing when leaving the page
  useEffect(() => () => observerRef.current?.disconnect(), []);

  // Read URL search params or route segment params
  const initialZone = searchParams.get("zone") || zoneSegment || "";
  const initialSearch = searchParams.get("search") || searchParams.get("name") || searchParams.get("q") || searchSegment || "";
  const initialRanking = searchParams.get("ranking") === "true";

  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [selectedZone, setSelectedZone] = useState(initialZone);
  const [rankingFilter, setRankingFilter] = useState(initialRanking);

  const [deleteId, setDeleteId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const label = { inputProps: { 'aria-label': 'Checkbox demo' } };

  // menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuPlaceId, setMenuPlaceId] = useState(null);
  const menuOpen = Boolean(menuAnchorEl);

  // Fetch available zones from API once
  useEffect(() => {
    getActiveZones()
      .then((res) => {
        const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        setAvailableZones(list.filter(Boolean));
      })
      .catch((err) => {
        console.error("Failed to fetch active zones", err);
      });
  }, []);

  // Sync state if URL changes externally (e.g. Back/Forward navigation, dashboard links)
  useEffect(() => {
    const urlZone = searchParams.get("zone") || zoneSegment || "";
    const urlSearch = searchParams.get("search") || searchParams.get("name") || searchParams.get("q") || searchSegment || "";
    const urlRanking = searchParams.get("ranking") === "true";

    setSelectedZone((prev) => (prev !== urlZone ? urlZone : prev));
    setSearchTerm((prev) => (prev !== urlSearch ? urlSearch : prev));
    setRankingFilter((prev) => (prev !== urlRanking ? urlRanking : prev));
  }, [searchParams, zoneSegment, searchSegment]);

  // Keep URL in sync with state changes (debounced for search)
  const isFirstMount = useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }

    const timer = setTimeout(() => {
      const nextParams = new URLSearchParams();
      if (selectedZone) nextParams.set("zone", selectedZone);
      if (searchTerm) nextParams.set("search", searchTerm);
      if (rankingFilter) nextParams.set("ranking", "true");

      const nextQuery = nextParams.toString();
      const currentQuery = searchParams.toString();

      if (nextQuery !== currentQuery) {
        navigate(`/places/view${nextQuery ? `?${nextQuery}` : ""}`, { replace: true });
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [searchTerm, selectedZone, rankingFilter, navigate, searchParams]);

  // Fetch data with pagination parameters
  useEffect(() => {
    const filters = {};
    if (searchTerm) filters.search = searchTerm;
    if (selectedZone) filters.zone = selectedZone;

    // Debounce typing and ignore responses that arrive after the filters changed or the page was left
    let cancelled = false;
    const timer = setTimeout(() => {
      getAllplaces(1, 1000, filters)
        .then((res) => {
          if (!cancelled) dispatch(setAllPlaces(res));
        }).catch((err) => {
          console.error("Failed to fetch places", err);
        });
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchTerm, selectedZone, dispatch]);

  // Combine zones from activeZones API + allPlaces in store
  const zones = [
    ...new Set([
      ...availableZones,
      ...(Array.isArray(allPlaces) ? allPlaces : []).map((place) => place?.zone).filter(Boolean),
    ]),
  ];

  // Client side sorting and filtering
  useEffect(() => {
    let filtered = [...(Array.isArray(allPlaces) ? allPlaces : [])].filter(Boolean);

    if (searchTerm) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((place) =>
        (place?.name || "").toLowerCase().includes(term) ||
        (place?.label || "").toLowerCase().includes(term)
      );
    }

    if (selectedZone && selectedZone !== "All") {
      const targetZone = selectedZone.toLowerCase();
      filtered = filtered.filter((place) => (place?.zone || "").toLowerCase() === targetZone);
    }

    if (rankingFilter) {
      filtered.sort((a, b) => {
        const aRank = a?.ranking ?? 0;
        const bRank = b?.ranking ?? 0;
        if (aRank === 0 && bRank === 0) return 0;
        if (aRank === 0) return 1;
        if (bRank === 0) return -1;
        return aRank - bRank;
      });
    }

    setFilteredPlaces(filtered);
  }, [searchTerm, selectedZone, rankingFilter, allPlaces]);

  const handleEdit = async (id) => {
    const response = await getSinglePlace(id);
    dispatch(setSelectedPlace(response));
    navigate(`/places/createandedit?id=${id}`);
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const handleImageUpload = (id) => {
    navigate(`/upload/destination/${id}`);
  };

  const confirmDelete = async () => {
    try {
      await deletePlace(deleteId);
      dispatch(removePlaceFromStore(deleteId));
      setConfirmOpen(false);
      showSnackbar("Place Deleted successfully", "success");
    } catch (err) {
      console.error("Failed to delete place", err);
      showSnackbar("Failed to delete place", "error");
    }
  };

  // menu handlers
  const handleMenuOpen = (e, placeId) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    setMenuAnchorEl(e.currentTarget);
    setMenuPlaceId(placeId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuPlaceId(null);
  };

  const handleAddPackages = () => {
    if (menuPlaceId) {
      navigate(`/upload/packages/${menuPlaceId}`);
    }
    handleMenuClose();
  };

  const paginatedRows = (filteredPlaces || []).filter(Boolean).slice(0, visibleCount);

  const packageToDelete = paginatedRows.find(pkg => pkg?._id === deleteId);

  return (
    <Container maxWidth={false} disableGutters>
      <Box display="flex" flexDirection="column" minHeight="100vh">

      <Box
        px={3}
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          bgcolor: "background.default",
          pt: 2,
          // borderBottom: "1px solid",
          // borderColor: "divider",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ mb: 3 }}
        >
          {/* Title */}
          <Typography variant="h5" fontWeight={850} color="primary.main" sx={{ letterSpacing: '-0.5px' }}>
            All Places
          </Typography>

          {/* Filters grouped in the middle */}
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <TextField
              label="Search by Name"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                endAdornment: searchTerm ? (
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm("")}
                    sx={{ p: 0.5 }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ) : null,
              }}
            />
            <TextField
              label="Filter by Zone"
              variant="outlined"
              size="small"
              select
              value={selectedZone}
              onChange={(e) => {
                const z = e.target.value;
                setSelectedZone(z);
                if (!z) setRankingFilter(false);
              }}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="">All Zones</MenuItem>
              {zones.map((zone, index) => (
                <MenuItem key={index} value={zone}>
                  {zone}
                </MenuItem>
              ))}
            </TextField>
            <Typography
              color={selectedZone ? "textPrimary" : "text.disabled"}
              display="flex"
              alignItems="center"
              gap={1}
              variant="body2"
            >
              Ranking
              <Checkbox
                {...label}
                disabled={!selectedZone}
                checked={rankingFilter}
                onChange={(e) => setRankingFilter(e.target.checked)}
                size="small"
              />
            </Typography>
          </Stack>

          {/* Action Button at the end */}
          {getPermission('places', 'create') && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => {
                dispatch(removeSelectedPlace());
                navigate("/places/createandedit");
              }}
            >
              Create New Place
            </Button>
          )}
        </Stack>
      </Box>

      {/* Main content */}
      <Box flex="1" px={3} sx={{ overflowY: 'auto' }}>

        {/* Cards */}
        {paginatedRows.length === 0 ? <Typography>No Data Found</Typography> :
          <Grid container spacing={3}>
            {paginatedRows.map((place, index) => (
              <Grid item xs={12} sm={6} md={4} key={place?._id || index}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", display: "flex", flexDirection: "column" }}
                >
                  <CardContent sx={{ pb: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                      <Typography
                        variant="h6"
                        component="div"
                        sx={{
                          lineHeight: 1.3,
                          wordBreak: "break-word",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                          flex: 1,
                        }}
                      >
                        {place?.name || "Unnamed Place"}
                        {place?.ranking >= 1 && (
                          <Chip size="small" label={place.ranking} color="success" variant="outlined" />
                        )}
                      </Typography>

                      {canAddPackages && (
                        <IconButton
                          aria-label="more options"
                          size="small"
                          onClick={(e) => handleMenuOpen(e, place?._id || place?.id)}
                          sx={{
                            color: "text.secondary",
                            p: 0.75,
                            mt: -0.5,
                            mr: -0.5,
                            flexShrink: 0,
                            touchAction: "manipulation",
                            "&:hover": { bgcolor: "action.hover" },
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>

                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Zone: {place?.zone || "N/A"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Temp: {place?.min_temperature ?? "-"}°C - {place?.max_temperature ?? "-"}°C
                    </Typography>
                    <Typography variant="body2" mt={1} noWrap>
                      Description: {place?.description || "No description"}
                    </Typography>
                    <Box mt={1}>
                      <Chip size="small" label={`Price: ₹${place?.price ?? 0}`} sx={{ mr: 1 }} />
                      {place?.is_active && <Chip size="small" label="Active" color="success" />}
                    </Box>
                  </CardContent>

                  <CardActions sx={{ mt: "auto", justifyContent: "space-between", px: 2, pb: 2 }}>
                    {getPermission('places', 'alter') &&
                      <Button size="small" variant="outlined" onClick={() => handleEdit(place?._id)}>
                        Edit
                      </Button>
                    }

                    {getPermission('places', 'alter-image') &&
                      <Badge badgeContent={place?.images?.length || 0} color="error" invisible={!place?.images || place.images.length === 0}>
                        <Button
                          size="small"
                          variant={(place?.images?.length || 0) > 0 ? "contained" : "outlined"}
                          color={(place?.images?.length || 0) > 0 ? "success" : "primary"}
                          startIcon={(place?.images?.length || 0) > 0 ? <CheckIcon /> : null}
                          onClick={() => handleImageUpload(place?._id)}
                        >
                          Upload
                        </Button>
                      </Badge>
                    }

                    {getPermission('places', 'alter-ranking') &&

                      <Select
                        size="small"
                        value={place?.ranking ?? 0}
                        onChange={(e) =>
                          handleChangeRanking(
                            place,
                            e.target.value,
                            allPlaces,
                            (updated) => dispatch(setAllPlaces(updated)),
                            rankingLoading,
                            setRankingLoading
                          )
                        }
                        disabled={Boolean(rankingLoading[place?._id || place?.id])}
                        sx={{
                          minWidth: 80,
                          height: 32,
                          "& .MuiSelect-select": { paddingY: 0.5 },
                        }}
                      >
                        {Array.from({ length: 11 }).map((_, i) => (
                          <MenuItem key={i} value={i}>
                            {i}
                          </MenuItem>
                        ))}
                      </Select>
                    }

                    {getPermission('places', 'delete') &&

                      <Button size="small" color="error" variant="outlined" onClick={() => handleDelete(place?._id)}>
                        Delete
                      </Button>
                    }
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        }
      </Box>

      {/* Pagination bar at bottom */}
      <div ref={lastElementRef} style={{ height: "20px" }}></div>

      {/* Per-card menu */}
      {canAddPackages && (
        <Menu
          anchorEl={menuAnchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{
            paper: {
              sx: {
                minWidth: 140,
                boxShadow: 3,
              },
            },
          }}
        >
          <MenuItem onClick={handleAddPackages}>Add Packages</MenuItem>
        </Menu>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete{" "}
          <strong>{packageToDelete?.name || "this package"}</strong>?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <SnackbarComponent />
      </Box>
    </Container>
  );
};

export default AllPlaces;
